import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BiotechIcon from "@mui/icons-material/Biotech";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ScienceIcon from "@mui/icons-material/Science";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SchoolIcon from "@mui/icons-material/School";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";

const Research = () => {
  const navigate = useNavigate();

  const overviewRef = useRef(null);
  const areasRef = useRef(null);
  const journalRef = useRef(null);
  const contactRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sidebarSections = [
    {
      id: "overview",
      label: "About",
      ref: overviewRef,
      icon: <InfoOutlinedIcon />,
    },
    {
      id: "areas",
      label: "Research Areas",
      ref: areasRef,
      icon: <BiotechIcon />,
    },
    {
      id: "journal",
      label: "Academic Journal",
      ref: journalRef,
      icon: <MenuBookIcon />,
    },
    {
      id: "contact",
      label: "Contact",
      ref: contactRef,
      icon: <MailOutlineIcon />,
    },
  ];

  const researchAreas = [
    {
      icon: <ScienceIcon sx={{ fontSize: 32 }} />,
      title: "Natural Sciences & Technology",
      color: "#1A237E",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      title: "Social Sciences",
      color: "#E53935",
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 32 }} />,
      title: "Business and Economics",
      color: "#1A237E",
    },
    {
      icon: <TheaterComedyIcon sx={{ fontSize: 32 }} />,
      title: "Cultural Studies",
      color: "#E53935",
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 32 }} />,
      title: "Political Sciences",
      color: "#1A237E",
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 32 }} />,
      title: "Education",
      color: "#E53935",
    },
    {
      icon: <AutoStoriesIcon sx={{ fontSize: 32 }} />,
      title: "Humanities",
      color: "#1A237E",
    },
  ];

  return (
    <>
      <PageHeader
        title="Research"
        subtitle="Fostering critical thinking, academic development, and the generation of knowledge relevant to education, business, and society."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Research", href: "/research" },
        ]}
      />

      <Container maxWidth="xl" sx={{ mb: 8, mt: 4 }}>
        <Grid container spacing={4}>
          {/* Sidebar Navigation */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                position: "sticky",
                top: 100,
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #1A237E 0%, #0d1657 100%)",
                  color: "white",
                  p: 2.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "1.1rem", fontWeight: 600 }}
                >
                  In this Section
                </Typography>
              </Box>
              <List sx={{ py: 1 }}>
                {sidebarSections.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    <ListItemButton
                      onClick={() => scrollToSection(section.ref)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        "&:hover": {
                          backgroundColor: "rgba(26, 35, 126, 0.08)",
                          borderLeft: "4px solid #E53935",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: "#1A237E" }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>

            {/* HERO INTRO */}
            <Box sx={{ mb: 8 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #1A237E 0%, #283593 100%)",
                  color: "white",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 200,
                    height: 200,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -60,
                    right: 80,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    background: "rgba(229,57,53,0.2)",
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: "1.8rem", md: "2.4rem" },
                    position: "relative",
                  }}
                >
                  Research at American College
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    maxWidth: 700,
                    opacity: 0.92,
                    position: "relative",
                  }}
                >
                  At American College, we strive to promote critical thinking,
                  academic development, and the generation of knowledge relevant
                  to education, business, and society. Through student academic
                  publications and institutional studies, the college
                  strengthens a culture of research that contributes to our
                  students' academic development.
                </Typography>
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    position: "relative",
                  }}
                >
                  <Button
                    variant="contained"
                    endIcon={<MenuBookIcon />}
                    onClick={() => navigate("/research/journal")}
                    sx={{
                      backgroundColor: "#E53935",
                      color: "white",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.25,
                      borderRadius: 2,
                      "&:hover": { backgroundColor: "#C62828" },
                    }}
                  >
                    View Academic Journals
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<MailOutlineIcon />}
                    onClick={() => scrollToSection(contactRef)}
                    sx={{
                      borderColor: "rgba(255,255,255,0.6)",
                      color: "white",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.25,
                      borderRadius: 2,
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Contact Research Department
                  </Button>
                </Box>
              </Paper>
            </Box>

            {/* ABOUT */}
            <Box ref={overviewRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#1A237E",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <InfoOutlinedIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#1A237E",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  About
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, rgba(26,35,126,0.02) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#555",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                  }}
                >
                  The Research Department supports the development of research
                  skills among students and faculty through various academic
                  projects. Our activities are designed to encourage critical
                  thinking, evidence-based analysis, and engagement with
                  contemporary social, educational, and business issues.
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 6 }} />

            {/* RESEARCH AREAS */}
            <Box ref={areasRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#E53935",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <BiotechIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#1A237E",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Research Areas
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {researchAreas.map((area, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        border: "1px solid #E0E0E0",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: `0 8px 24px ${area.color}20`,
                          borderColor: area.color,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            backgroundColor: `${area.color}12`,
                            color: area.color,
                            width: 52,
                            height: 52,
                            flexShrink: 0,
                          }}
                        >
                          {area.icon}
                        </Avatar>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            lineHeight: 1.3,
                          }}
                        >
                          {area.title}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 6 }} />

            {/* ACADEMIC JOURNAL */}
            <Box ref={journalRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#1A237E",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <MenuBookIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#1A237E",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Academic Journal of American College
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <Box
                  sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #1A237E15 0%, #1A237E05 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1A237E",
                      flexShrink: 0,
                    }}
                  >
                    <LibraryBooksIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#555",
                        fontSize: "1.05rem",
                        lineHeight: 1.8,
                        mb: 2,
                      }}
                    >
                      The Academic Journal serves as the institution's official
                      publication to share student research, institutional
                      studies, academic essays, and other scholarly
                      contributions from our academic body. A special emphasis
                      is given to our students' research projects and studies,
                      as those make up the majority of the Academic Journal's
                      content.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip label="Biannual Publication" size="small" sx={{ backgroundColor: "#1A237E10", color: "#1A237E", fontWeight: 500 }} />
                      <Chip label="Student Research" size="small" variant="outlined" />
                      <Chip label="Open Access" size="small" variant="outlined" />
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate("/research/journal")}
                  sx={{
                    backgroundColor: "#1A237E",
                    color: "white",
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "#0d1657" },
                  }}
                >
                  Browse Journal Issues
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 6 }} />

            {/* CONTACT */}
            <Box ref={contactRef} sx={{ mb: 6, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#E53935",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <MailOutlineIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#1A237E",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Contact our Research Department
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{ color: "#666", fontSize: "1.05rem", mb: 4, lineHeight: 1.8 }}
              >
                For questions regarding research projects or academic writing
                support, please contact the Research Department.
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    role: "Research Coordinator",
                    email: "research@americancollege.edu.sv",
                    color: "#1A237E",
                  },
                  {
                    role: "Research Assistant",
                    email: "jorge@americancollege.edu.sv",
                    color: "#E53935",
                  },
                ].map((contact, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        border: `2px solid ${contact.color}`,
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${contact.color} 0%, ${contact.color}dd 100%)`,
                          p: 2,
                          color: "white",
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {contact.role}
                        </Typography>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <MailOutlineIcon sx={{ color: contact.color, fontSize: 20 }} />
                          <Typography
                            component="a"
                            href={`mailto:${contact.email}`}
                            variant="body1"
                            sx={{
                              color: contact.color,
                              fontWeight: 600,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {contact.email}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Research;